import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, ArrowUpRight, ChevronUp } from 'lucide-react';
import LOGO_URL from '../assets/images/logo.png';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

// const LOGO_URL = "https://customer-assets.emergentagent.com/job_c14315c5-b515-4409-9527-d339fd8e0c36/artifacts/0dyv0gle_E11F06E7-E44E-4FA3-9E63-0578FEA1BA85.PNG";

// Certification badges
const CERT_BADGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_c14315c5-b515-4409-9527-d339fd8e0c36/artifacts/99zdyboc_mbe-certification.png",
    alt: "MBE Certification"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_c14315c5-b515-4409-9527-d339fd8e0c36/artifacts/6ezj0k0t_nvbdc-logo-horiz2.png",
    alt: "NVBDC Certified"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_c14315c5-b515-4409-9527-d339fd8e0c36/artifacts/t4b2wnlh_Service-Disabled%20Veteran-Owned-Certified.png",
    alt: "Service-Disabled Veteran-Owned Certified"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_c14315c5-b515-4409-9527-d339fd8e0c36/artifacts/5ogdaof5_Veteran-Owned%20Certified.png",
    alt: "Veteran-Owned Certified"
  }
];

export const Footer = () => {
  const { siteSettings } = useSiteSettings();
  const currentYear = new Date().getFullYear();
  const logoUrl = siteSettings?.logo || LOGO_URL;
  const [showScrollTop, setShowScrollTop] = useState(false);

  const footerLinks = {
    services: [
      { name: 'Clinical Trial Management', path: '/services#clinical-trial' },
      { name: 'Biostatistics & Data', path: '/services#biostatistics' },
      { name: 'Regulatory Affairs', path: '/services#regulatory' },
      { name: 'Medical Writing', path: '/services#medical-writing' },
      { name: 'Staffing Solutions', path: '/staffing' },
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Technology', path: '/technology' },
      { name: 'Case Studies', path: '/case-studies' },
      { name: 'Careers', path: '/careers' },
      { name: 'Contact', path: '/contact' },
    ],
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="bg-[#1B1F2C] border-t border-[#2E3446]" data-testid="footer">
      {/* Certifications Section */}
      <div className="border-b border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20 py-10">
          <p className="text-center text-sm text-[#9F999F] mb-8 uppercase tracking-wider">Certifications & Affiliations</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {CERT_BADGES.map((badge, index) => (
              <img 
                key={index}
                src={badge.src} 
                alt={badge.alt}
                className="h-16 md:h-20 w-auto object-contain bg-white p-3 rounded-lg opacity-90 hover:opacity-100 transition-opacity"
                data-testid={`cert-badge-${index}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="w-full px-6 sm:px-12 lg:px-20 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-1 text-center lg:text-left center" data-testid="footer-logo">
              <img src={logoUrl} alt="VetForce Solutions" style={{width:"330px",alignItems:"center"}}/>
            </Link>
            <p className="text-[#C9C9CD] text-sm mb-6 leading-relaxed">
              From Service to Science and Advanced Innovation. Veteran-led clinical research excellence.
            </p>
            <div className="flex gap-3">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-[#1B1F2C] border border-[#2E3446] flex items-center justify-center text-[#C9C9CD] hover:text-[#8F262B] hover:border-teal-500/50 transition-all"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-[#1B1F2C] border border-[#2E3446] flex items-center justify-center text-[#C9C9CD] hover:text-[#8F262B] hover:border-teal-500/50 transition-all"
                data-testid="footer-twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-silver-shine font-semibold mb-6 flex items-center gap-2">
              Services
              <div className="flex-1 h-px bg-gradient-to-r from-[#8F262B]/50 to-transparent ml-2" />
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-[#C9C9CD] hover:text-[#8F262B] text-sm transition-colors flex items-center gap-1 group"
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-silver-shine font-semibold mb-6 flex items-center gap-2">
              Company
              <div className="flex-1 h-px bg-gradient-to-r from-[#8F262B]/50 to-transparent ml-2" />
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-[#C9C9CD] hover:text-[#8F262B] text-sm transition-colors flex items-center gap-1 group"
                    data-testid={`footer-company-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-silver-shine font-semibold mb-6 flex items-center gap-2">
              Contact
              <div className="flex-1 h-px bg-gradient-to-r from-[#8F262B]/50 to-transparent ml-2" />
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-[#8F262B]" />
                </div>
                <span className="text-[#C9C9CD] text-sm">
                  111 N Orange Ave Suite 850<br />
                  Orlando, FL 32801
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-[#8F262B]" />
                </div>
                <a href="tel:407-777-2216" className="text-[#C9C9CD] hover:text-[#8F262B] text-sm transition-colors" data-testid="footer-phone">
                  407-777-2216
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#8F262B]" />
                </div>
                <a href="mailto:info@vetforcesolutions.com" className="text-[#C9C9CD] hover:text-[#8F262B] text-sm transition-colors" data-testid="footer-email">
                  info@vetforcesolutions.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#2E3446] mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#9F999F] text-sm">
              &copy; {currentYear} VetForce Solutions LLC. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-[#9F999F] hover:text-[#8F262B] text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-[#9F999F] hover:text-[#8F262B] text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-[#8F262B] to-[#DC2F3C] hover:from-[#7F070E] hover:to-[#B0252D] rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300 z-50 group"
          aria-label="Scroll to top"
          data-testid="scroll-to-top-btn"
        >
          <ChevronUp className="w-5 h-5 group-hover:scale-110 transition-transform" />
        </button>
      )}
    </footer>
  );
};
